import { cn, initialsFrom } from '@syncanix/ui';
import { Check, ChevronDown, Plus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { useT } from '@/core/providers/I18nProvider';
import { useRovingMenu } from '@/hooks/useRovingMenu';

import type { Workspace } from '@/core/providers/WorkspaceProvider';
import type { ReactElement } from 'react';

export interface WorkspaceSwitcherProps {
  workspaces: readonly Workspace[];
  current: Workspace | null;
  onSelect: (id: string) => void;
  /**
   * Invoked when the user picks "Create workspace". When provided, the
   * dropdown ends with a create action that routes to the onboarding wizard
   * (A14 — the create entry point that was promised here but never shipped).
   */
  onCreateWorkspace?: () => void;
  className?: string;
}

/**
 * Up to two uppercase initials from the displayed workspace label
 * ("Acme Corp" → "AC", "Personal" → "P"), falling back to "?" for a label that
 * cannot be read so the gradient tile is never empty.
 *
 * Delegates to the shared `initialsFrom` rather than deriving them here. The
 * local copy this replaces took the first CODE POINT of each part, so every
 * environment-qualified workspace drew a BRACKET as its second initial
 * ("Argus (dev)" → "A("). That was the same defect the shared helper carried,
 * in a second place — `UserMenu` had already moved to the shared avatar and
 * left this copy behind, and a duplicate of a rule is a second place for it to
 * be wrong. One behavioural difference comes with the move: for a name of three
 * or more words the initials are now the first and LAST part rather than the
 * first two ("Acme Widget Corp" → "AC", not "AW"). One- and two-word names —
 * which is every workspace name the product actually renders — are unchanged.
 */
function workspaceInitials(label: string): string {
  return initialsFrom(label) || '?';
}

/**
 * Dropdown of the user's workspaces. Closed state shows a brand-gradient
 * initial avatar + the current workspace name as the trigger; open state lists
 * workspaces with a check mark next to the active one, then a "Create
 * workspace" action (when `onCreateWorkspace` is wired) that routes to the
 * onboarding wizard. Closed on outside click or Escape.
 */
export function WorkspaceSwitcher({
  workspaces,
  current,
  onSelect,
  onCreateWorkspace,
  className,
}: WorkspaceSwitcherProps): ReactElement {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  // WAI-ARIA menu keyboard contract: focus the first item on open, arrow keys
  // cycle, Home/End jump, roving tabindex. (Open/close + Escape stay below.)
  useRovingMenu(menuRef, open);

  useEffect(() => {
    if (!open) return;
    function handleClick(event: MouseEvent): void {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function handleKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const triggerLabel =
    current?.name ??
    (workspaces.length === 0 ? t('workspace.noWorkspace') : t('workspace.defaultLabel'));

  return (
    // inline-FLEX (not inline-block): a block container shrinking as a flex item
    // does not constrain its inline-flex child, so on a 375px bar the trigger
    // painted past the container under the env switcher. As a flex container the
    // trigger becomes a shrinkable flex item and the label truncates instead.
    <div ref={rootRef} className={cn('relative inline-flex min-w-0 text-start', className)}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        // The visible label is just the workspace name ("Acme Corp"); the
        // aria-label prefixes it with context so a screen reader announces
        // "Workspace: Acme Corp, button", not a bare, ambiguous name.
        aria-label={t('workspace.switcherLabel', { name: triggerLabel })}
        onClick={() => {
          setOpen((prev) => !prev);
        }}
        // `min-w-0` + a flexible (not max-capped) label lets the trigger shrink
        // and truncate inside a tight top bar instead of overflowing it.
        className="inline-flex min-w-0 max-w-[12rem] items-center gap-2 rounded-md border border-hairline bg-surface px-2 py-1 text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:border-ring focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      >
        {/* Brand-gradient initial avatar. Decorative (the workspace name beside
            it is the accessible label, mirroring the UserMenu avatar) — the flex
            `gap-2` spaces it from the name, so it mirrors correctly in RTL.
            Hidden below sm: on a 375px bar those 36px are better spent on the
            workspace NAME (which otherwise truncates to a letter or two). */}
        {/* The gradient is the RING, not the fill. Initials painted straight
            onto `bg-brand-gradient` measure 1.80:1 against white at the sweep's
            cyan end — and no ink clears AA across the whole sweep, so there is
            no foreground that fixes it (see `ui-design.md` §6 and
            `Avatar.test.tsx`). A 1.5px inset with an opaque tile inside keeps
            the brand mark and gives the letters a token surface to sit on. */}
        <span
          aria-hidden="true"
          data-testid="workspace-avatar"
          className="hidden h-7 w-7 shrink-0 rounded-md bg-brand-gradient p-[1.5px] sm:block"
        >
          <span className="flex h-full w-full items-center justify-center rounded-sm bg-surface-well text-[11px] font-semibold text-foreground">
            {workspaceInitials(triggerLabel)}
          </span>
        </span>
        <span className="min-w-0 truncate">{triggerLabel}</span>
        <ChevronDown aria-hidden="true" className="h-4 w-4 shrink-0 text-muted-foreground" />
      </button>

      {open ? (
        <ul
          ref={menuRef}
          role="menu"
          aria-label={t('workspace.menuLabel')}
          // p-1 inset + rounded rows: the hover fill sits inside the panel's
          // radius instead of bleeding square-edged to the border.
          // `rounded-md` (8px) — the floating-menu corner, matching the two
          // Radix overlay primitives and `UserMenu` beside it. See the radius
          // note in `styles.css` for why a menu is tighter than the panel it
          // floats over.
          className="absolute start-0 z-40 mt-2 max-h-72 w-64 overflow-auto rounded-md border border-hairline bg-surface p-1 shadow-elevation-2 focus-visible:outline-none motion-safe:animate-fade-in"
        >
          {workspaces.length === 0 ? (
            <li className="px-2 py-1.5 text-sm text-muted-foreground" role="none">
              {t('workspace.emptyState')}
            </li>
          ) : (
            workspaces.map((w) => {
              const selected = w.id === current?.id;
              return (
                <li key={w.id} role="none">
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={selected}
                    onClick={() => {
                      onSelect(w.id);
                      setOpen(false);
                    }}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted focus-visible:bg-muted focus-visible:outline-none',
                      selected ? 'font-semibold' : 'text-foreground',
                    )}
                  >
                    {/* Same gradient initials tile as the trigger, so a menu row
                        and the closed state read as the same object. */}
                    <span
                      aria-hidden="true"
                      className="block h-7 w-7 shrink-0 rounded-md bg-brand-gradient p-[1.5px]"
                    >
                      <span className="flex h-full w-full items-center justify-center rounded-sm bg-surface-well text-[11px] font-semibold text-foreground">
                        {workspaceInitials(w.name)}
                      </span>
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col items-start">
                      <span className="w-full truncate text-start">{w.name}</span>
                      <span className="w-full truncate text-start text-xs font-normal text-muted-foreground">
                        {w.slug}
                      </span>
                    </span>
                    {selected ? (
                      <Check aria-hidden="true" className="h-4 w-4 shrink-0 text-primary-strong" />
                    ) : null}
                  </button>
                </li>
              );
            })
          )}
          {onCreateWorkspace !== undefined ? (
            <>
              <li
                role="separator"
                aria-hidden="true"
                className="-mx-1 my-1 border-t border-hairline"
              />
              <li role="none">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    onCreateWorkspace();
                    setOpen(false);
                  }}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
                >
                  {/* Dashed placeholder tile keeps the create action aligned with
                      the workspace rows' avatar column. */}
                  <span
                    aria-hidden="true"
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-dashed border-hairline-strong text-muted-foreground"
                  >
                    <Plus className="h-4 w-4" />
                  </span>
                  {t('workspace.createNew')}
                </button>
              </li>
            </>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
